// src/components/SplashScreen.js
import React, { useState, useEffect, useRef } from 'react';

/**
 * SplashScreen — "Particles become the line"
 *
 * 1. Tons of airy warm-toned particles float dreamlike across the screen
 * 2. Wordmark fades in softly
 * 3. Particles begin converging — they stream toward the accent line position
 * 4. Particles pack into a tight horizontal line and SOLIDIFY — flash of light
 * 5. "with Danielle" appears from the flash
 * 6. Brief hold, then morph into the app (soft blur + fade)
 *
 * Timing:
 *   0ms     — Particles floating freely
 *   900ms   — Wordmark fades in
 *   1600ms  — Particles start converging toward line
 *   2800ms  — Line solidifies, flash, tagline appears
 *   3400ms  — Hold
 *   4000ms  — Begin exit morph
 *   4600ms  — Complete
 */

const TOTAL_DURATION = 4600;
const PARTICLE_COUNT = 80;
const LINE_WIDTH_PX = 56;

export default function SplashScreen({ onComplete }) {
    const [phase, setPhase] = useState(0);
    const canvasRef = useRef(null);
    const animRef = useRef(null);

    useEffect(() => {
        const t1 = setTimeout(() => setPhase(1), 900);      // Wordmark
        const t2 = setTimeout(() => setPhase(2), 1600);     // Converge
        const t3 = setTimeout(() => setPhase(3), 2800);     // Solidify + flash + tagline
        const t4 = setTimeout(() => setPhase(4), 4000);     // Exit
        const t5 = setTimeout(() => onComplete(), TOTAL_DURATION);
        return () => { [t1, t2, t3, t4, t5].forEach(clearTimeout); };
    }, [onComplete]);

    // Canvas particles
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        const dpr = window.devicePixelRatio || 1;
        const w = window.innerWidth;
        const h = window.innerHeight;
        canvas.width = w * dpr;
        canvas.height = h * dpr;
        ctx.scale(dpr, dpr);

        const cx = w / 2;
        const cy = h / 2;

        // Read colors
        const rootStyle = getComputedStyle(document.documentElement);
        const primaryRaw = rootStyle.getPropertyValue('--mom-primary').trim() || '#c4796b';
        const lightRaw = rootStyle.getPropertyValue('--mom-primary-light').trim() || '#d4938a';

        const hexToRgb = (hex) => {
            const c = hex.replace('#', '');
            return [parseInt(c.substring(0, 2), 16), parseInt(c.substring(2, 4), 16), parseInt(c.substring(4, 6), 16)];
        };
        const pc = hexToRgb(primaryRaw);
        const lc = hexToRgb(lightRaw);

        // The accent line sits below the wordmark — approximately 58% down from center
        // (wordmark is centered, line is ~44px below center)
        const lineY = cy + 44;
        const lineLeft = cx - LINE_WIDTH_PX / 2;
        const lineRight = cx + LINE_WIDTH_PX / 2;

        // Create particles with random starting positions across the whole screen
        const particles = Array.from({ length: PARTICLE_COUNT }, (_, i) => {
            // Random position anywhere on screen
            const startX = Math.random() * w;
            const startY = Math.random() * h;

            // Target: a point along the accent line
            const lineT = Math.random();
            const targetX = lineLeft + lineT * LINE_WIDTH_PX;
            const targetY = lineY + (Math.random() - 0.5) * 2;

            const size = 1.5 + Math.random() * 3;
            const useLight = Math.random() > 0.4;
            const baseColor = useLight ? lc : pc;
            const shift = () => Math.round((Math.random() - 0.5) * 30);

            return {
                x: startX, y: startY,
                startX, startY,
                targetX, targetY,
                size,
                baseSize: size,
                maxOpacity: 0.15 + Math.random() * 0.5,
                opacity: 0,
                r: Math.max(0, Math.min(255, baseColor[0] + shift())),
                g: Math.max(0, Math.min(255, baseColor[1] + shift())),
                b: Math.max(0, Math.min(255, baseColor[2] + shift())),
                // Floating drift
                driftAngle: Math.random() * Math.PI * 2,
                driftSpeed: 0.3 + Math.random() * 0.6,
                driftRadius: 20 + Math.random() * 40,
                phase: Math.random() * Math.PI * 2,
            };
        });

        let startTime = null;

        const animate = (timestamp) => {
            if (!startTime) startTime = timestamp;
            const elapsed = timestamp - startTime;
            ctx.clearRect(0, 0, w, h);

            // Phase timings (ms)
            const floatEnd = 1600;
            const convergeEnd = 2800;
            const solidifyEnd = 4000;

            particles.forEach(p => {
                if (elapsed < floatEnd) {
                    // FREE FLOAT — gentle dreamy drift
                    const t = elapsed / 1000;
                    p.x = p.startX + Math.sin(t * p.driftSpeed + p.phase) * p.driftRadius;
                    p.y = p.startY + Math.cos(t * p.driftSpeed * 0.7 + p.phase) * p.driftRadius * 0.6;
                    // Fade in over first 800ms
                    p.opacity = p.maxOpacity * Math.min(elapsed / 800, 1);
                    p.size = p.baseSize;
                } else if (elapsed < convergeEnd) {
                    // CONVERGE — lerp toward target on the line
                    const progress = (elapsed - floatEnd) / (convergeEnd - floatEnd);
                    // Ease-in-out with slight overshoot feel
                    const ease = progress < 0.5
                        ? 4 * progress * progress * progress
                        : 1 - Math.pow(-2 * progress + 2, 3) / 2;

                    const floatX = p.startX + Math.sin((floatEnd / 1000) * p.driftSpeed + p.phase) * p.driftRadius;
                    const floatY = p.startY + Math.cos((floatEnd / 1000) * p.driftSpeed * 0.7 + p.phase) * p.driftRadius * 0.6;

                    p.x = floatX + (p.targetX - floatX) * ease;
                    p.y = floatY + (p.targetY - floatY) * ease;
                    p.opacity = p.maxOpacity * (0.5 + ease * 0.5);
                    // Shrink as they converge
                    p.size = p.baseSize * (1 - ease * 0.6);
                } else if (elapsed < solidifyEnd) {
                    // SOLIDIFIED — particles stay at line, shrink to nothing quickly
                    const progress = (elapsed - convergeEnd) / 300; // fade out in 300ms
                    p.x = p.targetX;
                    p.y = p.targetY;
                    p.opacity = p.maxOpacity * Math.max(1 - progress, 0);
                    p.size = p.baseSize * 0.4 * Math.max(1 - progress, 0);
                } else {
                    // EXIT — particles gone
                    p.opacity = 0;
                }

                if (p.opacity <= 0.01) return;

                ctx.beginPath();
                ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
                ctx.fillStyle = `rgba(${p.r},${p.g},${p.b},${p.opacity})`;
                ctx.fill();
            });

            if (elapsed < TOTAL_DURATION) {
                animRef.current = requestAnimationFrame(animate);
            }
        };

        animRef.current = requestAnimationFrame(animate);
        return () => { if (animRef.current) cancelAnimationFrame(animRef.current); };
    }, []);

    const isExiting = phase >= 4;

    return (
        <div style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
            background: 'var(--mom-cream)',
            opacity: isExiting ? 0 : 1,
            transform: isExiting ? 'scale(1.03)' : 'scale(1)',
            filter: isExiting ? 'blur(6px)' : 'blur(0px)',
            transition: isExiting
                ? 'opacity 0.55s cubic-bezier(0.4, 0, 1, 1), transform 0.55s cubic-bezier(0.4, 0, 1, 1), filter 0.55s cubic-bezier(0.4, 0, 1, 1)'
                : 'none',
            pointerEvents: isExiting ? 'none' : 'auto',
            overflow: 'hidden',
        }}>
            {/* Particle canvas */}
            <canvas ref={canvasRef} style={{
                position: 'absolute', inset: 0, zIndex: 1,
                width: '100%', height: '100%',
            }} />

            {/* Soft ambient glow behind center — builds during convergence */}
            <div style={{
                position: 'absolute', zIndex: 2,
                width: 250, height: 150, borderRadius: '50%',
                background: 'radial-gradient(ellipse, color-mix(in srgb, var(--mom-primary) 12%, transparent) 0%, transparent 70%)',
                opacity: phase >= 2 ? (isExiting ? 0 : 0.8) : 0,
                transition: isExiting ? 'opacity 0.4s ease' : 'opacity 0.8s ease-out',
                transform: 'translateY(20px)',
            }} />

            {/* Wordmark — fades in during phase 1 */}
            <div style={{
                position: 'relative', zIndex: 5,
                opacity: phase >= 1 ? (isExiting ? 0 : 1) : 0,
                transform: phase >= 1 ? 'translateY(0)' : 'translateY(8px)',
                transition: isExiting
                    ? 'opacity 0.3s ease-out'
                    : 'opacity 0.7s ease-out, transform 0.7s cubic-bezier(0.16, 1, 0.3, 1)',
            }}>
                <div style={{
                    fontFamily: 'var(--mom-font-serif)',
                    fontSize: 'clamp(32px, 7vw, 48px)',
                    fontWeight: 700,
                    fontStyle: 'italic',
                    color: 'var(--mom-charcoal)',
                    letterSpacing: '-0.02em',
                    lineHeight: 1.1,
                    textAlign: 'center',
                }}>
                    moments of<br />movement
                </div>
            </div>

            {/* Accent line — appears when particles solidify (phase 3) */}
            <div style={{
                position: 'relative', zIndex: 5,
                height: 2,
                margin: '22px auto',
                borderRadius: 2,
                overflow: 'visible',
            }}>
                {/* The solid line */}
                <div style={{
                    width: phase >= 3 ? LINE_WIDTH_PX : 0,
                    height: '100%',
                    borderRadius: 2,
                    background: 'linear-gradient(90deg, var(--mom-primary), var(--mom-primary-light))',
                    margin: '0 auto',
                    transition: phase >= 3
                        ? 'width 0.3s cubic-bezier(0.16, 1, 0.3, 1)'
                        : 'none',
                    boxShadow: phase >= 3
                        ? '0 0 20px color-mix(in srgb, var(--mom-primary) 30%, transparent), 0 0 40px color-mix(in srgb, var(--mom-primary) 15%, transparent)'
                        : 'none',
                    opacity: isExiting ? 0 : 1,
                }} />

                {/* Flash burst when line solidifies */}
                {phase >= 3 && !isExiting && (
                    <div style={{
                        position: 'absolute',
                        top: '50%', left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 8, height: 8, borderRadius: '50%',
                        background: 'var(--mom-primary-light)',
                        animation: 'splashLineFlash 0.6s ease-out forwards',
                    }} />
                )}
            </div>

            {/* Tagline — appears with the flash */}
            <div style={{
                position: 'relative', zIndex: 5,
                opacity: phase >= 3 ? (isExiting ? 0 : 1) : 0,
                transform: phase >= 3 ? 'translateY(0)' : 'translateY(6px)',
                transition: isExiting
                    ? 'opacity 0.3s ease-out'
                    : 'opacity 0.5s ease-out 0.15s, transform 0.5s cubic-bezier(0.16, 1, 0.3, 1) 0.15s',
            }}>
                <div style={{
                    fontFamily: 'var(--mom-font-sans)',
                    fontSize: 11,
                    fontWeight: 600,
                    letterSpacing: '0.25em',
                    textTransform: 'uppercase',
                    color: 'var(--mom-warm-gray)',
                }}>
                    with Danielle
                </div>
            </div>

            {/* Keyframes */}
            <style>{`
                @keyframes splashLineFlash {
                    0% { transform: translate(-50%, -50%) scale(1); opacity: 1; box-shadow: 0 0 20px 8px color-mix(in srgb, var(--mom-primary) 40%, transparent); }
                    50% { transform: translate(-50%, -50%) scale(8, 3); opacity: 0.6; box-shadow: 0 0 40px 16px color-mix(in srgb, var(--mom-primary) 20%, transparent); }
                    100% { transform: translate(-50%, -50%) scale(12, 1); opacity: 0; box-shadow: none; }
                }
            `}</style>
        </div>
    );
}